import React from 'react'
import { useSnackbar } from 'notistack'
import { useAppSelector } from '../../hooks/redux'

import { initialState, notificationReducer, Actions } from './reducer'

import AppNotification from '../../components/molecules/AppNotification'
import useAudioClip from '../../hooks/useAudioClip'
import AppNotificationV2 from '../../components/molecules/AppNotificationV2'

import { selectDMs } from '../../slices/dms'
import { useAuth } from '../AuthProvider'
import { useBreakpoint } from '../BreakpointProvider'
import { useEnvironment } from '../EnvironmentProvider'

/**
 * The queue requires a notification to have these properties
 * @typedef {Object} NotificationData
 * @property {string?} muteOn - Path that this notification will not be shown on, null to always show
 * @property {string} type - One of {@link TYPES}
 * @property {string} message - What the notification should show
 * @property {string} id - Unique id for this message, to stop duplicates from showing
 * @property {string[]} users - Array of user IDs to show avatars
 * @property {string?} name - Optional, the title to be shown (becomes names of users otherwise)
 * @property {Object} msg - The full message object from the socket
 * @property {function?} onClick - Extra action to take when clicked
 */

const AppNotificationContext = React.createContext(null)

export const TYPES = {
  PEER: 'PEER',
  GROUP: 'GROUP',
  CONVERSATION: 'CONVERSATION',
  NOTIFICATION: 'NOTIFICATION',
  NEW_CHAT: 'NEW_CHAT',
  SURVEY: 'SURVEY',
  REMINDER: 'REMINDER'
}

const AppNotificationProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar()

  const { dms } = useAppSelector(selectDMs)

  const { pathName } = useAuth()
  const { isSmall } = useBreakpoint()
  const { getEnvValue } = useEnvironment()

  const playNormalClip = useAudioClip('/sounds/msg.mp3')
  const playPeerClip = useAudioClip('/sounds/peermsg.mp3')

  const [state, dispatch] = React.useReducer(notificationReducer, initialState)

  /** Queues a notification for showing */
  const showNotification = React.useCallback(
    /** @param {NotificationData} data - The notification to show */
    data => {
      // @ts-ignore
      dispatch({ type: Actions.ENQUEUE_NOTIFICATION, data })
    },
    []
  )
  const notificationsTopBar = document.querySelector(
    '[data-testid="notifications-top-bar"'
  )

  // Plays a sound if user settings allow
  const playSound = React.useCallback(
    isConversation => {
      if (isConversation) {
        const shouldPlayClient =
          getEnvValue('settings', 'n_client_sound_new_msg', 'false') === 'true'

        if (shouldPlayClient) {
          playNormalClip()
        }

        return
      }

      const shouldPlayPeer =
        getEnvValue('settings', 'n_peer_sound_new_msg', 'false') === 'true'

      if (shouldPlayPeer) {
        playPeerClip()
      }
    },
    [getEnvValue, playNormalClip, playPeerClip]
  )

  // Shows notifications from the queue
  React.useEffect(() => {
    if (!state.queue.length) {
      return
    }

    state.queue.forEach(item => {
      const {
        muteOn,
        type,
        message,
        users,
        name,
        msg,
        onClick,
        v2,
        forceAudio
      } = item

      // Play sound clip and show notification if necessary
      if (!muteOn || pathName !== muteOn || forceAudio) {
        if (type !== TYPES.NOTIFICATION) {
          const isConversation = type === TYPES.CONVERSATION
          playSound(isConversation)
          // audio has been played, leave.
          if (forceAudio && pathName === muteOn) {
            return
          }
        }

        // Queue the notification with notistack
        const notifProps = { type, message, users, name, msg, onClick }

        if (type === TYPES.NOTIFICATION) {
          const { source_session_id } = msg
          notifProps.isGroup = Boolean(dms[source_session_id])
        }
        if (v2) {
          let top = 0
          if (notificationsTopBar) {
            top = 40
          }
          enqueueSnackbar('alert-v2', {
            style: { left: isSmall ? 0 : 324, top },
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            autoHideDuration: 30000,
            content: id => <AppNotificationV2 id={id} {...notifProps} />
          })
        } else {
          enqueueSnackbar('alert', {
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            // @ts-ignore
            content: id => <AppNotification id={id} {...notifProps} />
          })
        }
      }
    })

    // @ts-ignore
    // All notifications have been handled at this point
    dispatch({ type: Actions.NOTIFICATIONS_SHOWN })
  }, [
    state.queue,
    pathName,
    playSound,
    enqueueSnackbar,
    dms,
    notificationsTopBar,
    isSmall
  ])

  return (
    <AppNotificationContext.Provider value={showNotification}>
      {children}
    </AppNotificationContext.Provider>
  )
}

export default AppNotificationProvider

export const useAppNotification = () => React.useContext(AppNotificationContext)
